import { LegendItem } from '@modules/charts/components/LegendItem';
import { LegendLabel } from '@modules/charts/types';
import { useGetColor } from '@modules/common/hooks';
import { GetChartLabelOptions } from '@modules/common/types';
import { Box, Button, Grid, Paper, Popper, Stack } from '@mui/material';
import { useState } from 'react';
import { useMeasure } from 'react-use';
import { FieldNames } from 'v2/core/entities/fields';

interface HorizontalLegendProps {
  labels: LegendLabel[];
  totalRecords?: number;
  fieldName?: FieldNames;
  getColor?: (index: number, label: LegendLabel) => string;
  getLabelOptions?: GetChartLabelOptions;
  shouldShowLegendsInNewRow?: boolean;
  onMouseEnter?: (event: React.MouseEvent, index: number) => void;
  onMouseLeave?: (event: React.MouseEvent) => void;
}

export const HorizontalLegend = ({
  labels,
  fieldName,
  totalRecords,
  getColor,
  getLabelOptions,
  shouldShowLegendsInNewRow = false,
  onMouseEnter,
  onMouseLeave,
}: HorizontalLegendProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const getChartColor = useGetColor({ fieldName });

  const handleOpenPopper = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const [legendRef, { width: legendWidth }] = useMeasure();
  const handleClosePopper = () => {
    setAnchorEl(null);
  };

  const getMaxLabelsToShow = () => {
    if (!legendWidth || shouldShowLegendsInNewRow) {
      return labels.length;
    }
    if (legendWidth > 420) {
      return 2;
    }
    return 1;
  };

  const visibleLabels = labels.slice(0, getMaxLabelsToShow());
  const hiddenLabels = labels.slice(getMaxLabelsToShow());
  const isPopperOpen = Boolean(anchorEl);
  const popperID = isPopperOpen ? 'other-legends-popper' : undefined;

  const renderHiddenLegendsInPopper = () => {
    if (!hiddenLabels?.length) {
      return null;
    }

    return (
      <Popper
        id={popperID}
        open={isPopperOpen}
        anchorEl={anchorEl}
        placement="top-start"
        sx={{
          zIndex: (theme) => theme.zIndex.tooltip,
        }}
      >
        <Paper sx={{ p: 1 }} elevation={10}>
          {hiddenLabels.map((label, index: number) => {
            const color = getColor
              ? getColor(index + visibleLabels.length, label)
              : getChartColor(index + visibleLabels.length, label.name);
            return (
              <Grid item key={label.id}>
                <LegendItem
                  id={label.id}
                  color={color}
                  label={label.name}
                  itemValue={label.value}
                  totalRecords={totalRecords}
                  layout="vertical"
                  variant="small"
                  getLabelOptions={getLabelOptions}
                />
              </Grid>
            );
          })}
        </Paper>
      </Popper>
    );
  };

  const renderOtherLegendsButton = () => {
    return (
      <Button
        size="small"
        sx={{
          color: isPopperOpen ? 'primary.main' : 'text.secondary',
          fontSize: '12px',
          whiteSpace: 'nowrap',
          minWidth: 'max-content',
        }}
        onMouseOver={handleOpenPopper}
        id={popperID}
        onMouseLeave={handleClosePopper}
        aria-describedby={popperID}
      >
        Others (+{hiddenLabels.length})
      </Button>
    );
  };

  const renderHiddenLegends = () => {
    if (hiddenLabels.length === 1) {
      const color = getColor
        ? getColor(labels.length - 1, hiddenLabels[0])
        : getChartColor(labels.length - 1, hiddenLabels[0].name);
      return (
        <LegendItem
          id={hiddenLabels[0].id}
          color={color}
          label={hiddenLabels[0].name}
          itemValue={hiddenLabels[0].value}
          totalRecords={totalRecords}
          getLabelOptions={getLabelOptions}
          layout="horizontal"
        />
      );
    }
    if (hiddenLabels.length > 1) {
      return (
        <Stack justifyContent="center" alignItems="center" width="100%">
          {renderOtherLegendsButton()}
          {renderHiddenLegendsInPopper()}
        </Stack>
      );
    }
    return null;
  };

  const getItemColumns = () => {
    if (shouldShowLegendsInNewRow) {
      return 'auto';
    }
    if (hiddenLabels.length) {
      return 12 / (visibleLabels.length + 1);
    }
    return 12 / visibleLabels.length;
  };

  return (
    <Box ref={legendRef} width="100%">
      <Grid
        container
        direction="row"
        alignItems="center"
        justifyContent={shouldShowLegendsInNewRow ? 'start' : 'center'}
        sx={{
          height: '100%',
          overflow: 'auto',
        }}
        spacing={shouldShowLegendsInNewRow ? 1 : 'inherit'}
      >
        {visibleLabels?.map((label, index: number) => {
          const color = getColor
            ? getColor(index, label)
            : getChartColor(index, label.name);
          return (
            <Grid
              item
              xs={getItemColumns()}
              key={label.id}
              justifyContent="center"
            >
              <LegendItem
                id={label.id}
                label={label.name}
                itemValue={label.value}
                index={index}
                totalRecords={totalRecords}
                layout="horizontal"
                color={color}
                getLabelOptions={getLabelOptions}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
              />
            </Grid>
          );
        })}
        <Grid item xs={12 / (visibleLabels.length + 1)}>
          {renderHiddenLegends()}
        </Grid>
      </Grid>
    </Box>
  );
};
